import type { ReactElement } from 'react'
import type { SalesOrder, SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import CharacterCount from '@/components/form/CharacterCount'
import { OrderAsideFormFooter } from '@/components/orders/OrderAside'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import { ORDER_ASIDE_FORM_SURFACE_CLASS } from '@/components/orders/order-aside.styles'

import { serverFieldErrors, useAppForm } from '@/lib/form'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { getErrorMessage } from '@/lib/utils'
import { ScissorsLineDashedIcon } from 'lucide-react'
import { toast } from 'sonner'

function EditSpecialInstructionsForm({
  initialData,
  closeAside,
  salesOrderId,
}: {
  initialData?: Partial<Pick<SalesOrder, 'id' | 'special_instructions'>>
  closeAside?: () => void
  salesOrderId: SalesOrderIdInput
}): ReactElement {
  const { mutateAsync: updateSalesOrder, isPending } = useUpdateSalesOrderMutation()

  const form = useAppForm({
    defaultValues: {
      special_instructions: initialData?.special_instructions || '',
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          await updateSalesOrder({
            id: salesOrderId,
            special_instructions: value.special_instructions,
          })
          toast.success('Special instructions updated successfully')
          closeAside?.()
          return undefined
        } catch (error) {
          toast.error('Special instructions update failed', {
            description: getErrorMessage(error),
          })

          // Return validation errors to display them on the form fields
          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col pt-5 pl-0 sm:pt-6 sm:pl-1">
        <OrderDetailPanel
          title="Special Instructions"
          subtitle="Update notes for your team, delivery driver, or designer."
          icon={ScissorsLineDashedIcon}
          tone="periwinkle"
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <div className="grid gap-y-5">
              <div className="grid space-y-2">
                <form.AppField name="special_instructions">
                  {(field) => (
                    <div className="space-y-1">
                      <field.TextareaInput
                        id={`${initialData?.id}-special_instructions`}
                        placeholder="Anything your team should know before fulfilling this order?"
                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.special_instructions}
                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.special_instructions}
                        rows={5}
                      />
                      <CharacterCount
                        value={field.state.value}
                        maxLength={ORDER_FIELD_MAX_LENGTHS.special_instructions}
                      />
                    </div>
                  )}
                </form.AppField>
              </div>
            </div>
          </div>
        </OrderDetailPanel>
        <OrderAsideFormFooter
          form={form}
          mutation={{ isPending }}
          saveLabel="Save Instructions"
          onCancel={closeAside}
        />
      </form.Form>
    </form.AppForm>
  )
}

export default EditSpecialInstructionsForm
